import { Component, signal, inject, computed } from '@angular/core';
import {
  ModalController, IonButton, IonButtons, IonInput, IonGrid, IonRow, IonCol, IonIcon,
  IonHeader, IonToolbar, IonTitle, IonContent, IonCard, IonCardContent, IonSpinner
} from '@ionic/angular/standalone';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NicheService, ResolvedNiche } from '../../services/niche.service';

@Component({
  selector: 'app-onboarding-stepper',
  standalone: true,
  imports: [
    IonButton, IonButtons, IonInput, IonGrid, IonRow, IonCol, IonIcon,
    IonHeader, IonToolbar, IonTitle, IonContent,
    IonCard, IonCardContent,
    IonSpinner,
    FormsModule,
    TranslocoPipe,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ stepTitle() | transloco }}</ion-title>
        <ion-buttons slot="end">
          <ion-button fill="clear" color="medium" (click)="skip()" [attr.aria-label]="'general.close' | transloco">
            <ion-icon slot="icon-only" name="close-circle-outline" size="large"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding" style="--overflow: hidden;">
      <div class="onboarding-content">
        <div class="step-dots">
          @for (i of stepIndices; track i) {
            <span [class.active]="i === currentStep()"></span>
          }
        </div>

        <div class="onboarding-body">
          @if (currentStep() === 0) {
            <div class="step-item">
              <ion-icon name="rocket-outline" class="step-icon" color="primary"></ion-icon>
              <h2>{{ 'onboarding.step1Title' | transloco }}</h2>
              <p>{{ 'onboarding.step1P' | transloco }}</p>
              <ul class="features-list">
                @for (feature of (currentNiche()?.ui_config?.onboarding_features || []); track $index) {
                  <li>
                    <ion-icon name="flash-outline" color="primary"></ion-icon>
                    {{ feature }}
                  </li>
                }
              </ul>
            </div>
          } @else if (currentStep() === 1) {
            <div class="step-item niche-step">
              <ion-icon name="options-outline" class="step-icon" color="primary"></ion-icon>
              <h2 style="margin: 0.2em 0;">{{ 'onboarding.step2Title' | transloco }}</h2>
              <p style="margin:0px;">{{ 'onboarding.step2P' | transloco }}</p>
              <div class="niche-scroll-area">
                <ion-grid>
                  <ion-row>
                    @for (niche of availableNiches(); track niche.id) {
                      <ion-col size="6">
                        <ion-card
                          button
                          [class.selected]="nicheService.currentNiche()?.id === niche.id"
                          [class.loading]="niche.id === nicheService.updatingNicheId()"
                          (click)="selectNiche(niche)"
                          class="niche-card"
                        >
                          <ion-card-content>
                            @if (niche.id === nicheService.updatingNicheId()) {
                              <ion-spinner name="dots" class="spinner-md"></ion-spinner>
                            } @else {
                              <ion-icon [name]="niche.icon || 'layers-outline'" color="primary" size="large"></ion-icon>
                            }
                            <div>{{ niche.name }}</div>
                          </ion-card-content>
                        </ion-card>
                      </ion-col>
                    }
                  </ion-row>
                </ion-grid>
              </div>
            </div>
          } @else if (currentStep() === 2) {
            <div class="step-item">
              <ion-icon name="construct-outline" class="step-icon" color="primary"></ion-icon>
              <h2 style="margin: 0.2em 0;">{{ 'onboarding.step3Title' | transloco }}</h2>
              <p style="margin:0px;">{{ 'onboarding.step3P' | transloco }}</p>
              <ion-input [ngModel]="sampleInstruction()" [placeholder]="'onboarding.step3Placeholder' | transloco" class="sample-input"></ion-input>
              <div>
                <ion-button style="padding:0px;" color="primary" [disabled]="sampleRunning || sampleComplete" (click)="runSample()">{{ 'onboarding.step3RunButton' | transloco }}</ion-button>
              </div>

              @if (sampleRunning) {
                <div class="sample-messages">
                  <div class="chat-bubble">
                    <span class="bubble-text">{{ 'onboarding.sampleUnderstanding' | transloco }}</span>
                    <span class="loading-orb"></span>
                  </div>
                  <div class="chat-bubble">
                    <span class="bubble-text">{{ 'onboarding.sampleHeading' | transloco }}</span>
                    <span class="loading-orb"></span>
                  </div>
                  <div class="chat-bubble">
                    <span class="bubble-text">{{ 'onboarding.samplePackaging' | transloco }}</span>
                    <span class="loading-orb"></span>
                  </div>
                </div>
              }

              @if (sampleComplete) {
                <div class="sample-result">
                  <p>{{ 'onboarding.sampleCompleted' | transloco }}</p>
                  <ion-grid class="result-grid">
                    <ion-row class="table-header">
                      <ion-col><strong>{{ 'onboarding.sampleCity' | transloco }}</strong></ion-col>
                      <ion-col><strong>{{ 'onboarding.sampleTemperature' | transloco }}</strong></ion-col>
                      <ion-col><strong>{{ 'onboarding.sampleCondition' | transloco }}</strong></ion-col>
                    </ion-row>
                    <ion-row>
                      <ion-col>{{ 'onboarding.sampleCity1' | transloco }}</ion-col>
                      <ion-col>{{ 'onboarding.sampleTemp1' | transloco }}</ion-col>
                      <ion-col>{{ 'onboarding.sampleCondition1' | transloco }}</ion-col>
                    </ion-row>
                    <ion-row>
                      <ion-col>{{ 'onboarding.sampleCity2' | transloco }}</ion-col>
                      <ion-col>{{ 'onboarding.sampleTemp2' | transloco }}</ion-col>
                      <ion-col>{{ 'onboarding.sampleCondition2' | transloco }}</ion-col>
                    </ion-row>
                  </ion-grid>
                </div>
              }
            </div>
          } @else if (currentStep() === 3) {
            <div class="step-item">
              <ion-icon name="settings-outline" class="step-icon" color="primary"></ion-icon>
              <h2>{{ 'onboarding.step4Title' | transloco }}</h2>
              <p>{{ 'onboarding.step4P' | transloco }}</p>
            </div>
          }
        </div>

        <div class="onboarding-footer">
          <ion-button fill="clear" color="medium" (click)="skip()">{{ 'onboarding.skip' | transloco }}</ion-button>
          <ion-button color="primary" [disabled]="isNextDisabled()" (click)="nextStep()" class="modal-confirm-btn">
            {{ nextLabel() | transloco }}
          </ion-button>
        </div>
      </div>
    </ion-content>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .onboarding-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      text-align: center;
      padding: 0 1em;
      gap: 1.5em;
    }

    .step-dots {
      display: flex;
      gap: 0.4em;
    }
    .step-dots span {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--onboarding-dot-inactive);
    }
    .step-dots span.active {
      background: var(--ion-color-primary);
    }

    .onboarding-body {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 441px;
    }

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }

    .step-icon {
      font-size: 48px;
    }

    .features-list {
      list-style: none;
      padding: 0;
      margin: 1em 0 0;
      display: flex;
      flex-direction: column;
      gap: 0.6em;
    }
    .features-list li {
      display: flex;
      align-items: center;
      gap: 0.4em;
    }

    .sample-input {
      width: 100%;
    }

    .sample-messages {
      width: 100%;
    }

    .chat-bubble {
      display: flex;
      align-items: center;
      gap: 0.5em;
      padding: 0.6em 0.8em;
      background: var(--onboarding-bubble-bg);
      border-radius: 12px;
      margin-bottom: 0.5em;
    }
    .bubble-text {
      flex: 1;
    }
    .loading-orb {
      width: 1em;
      height: 1em;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
      animation: orbPulse 1.5s ease-in-out infinite;
      box-shadow: 0 0 10px rgba(var(--ion-color-primary-rgb), 0.5);
      flex-shrink: 0;
    }

    .sample-result {
      width: 100%;
    }
    .result-grid {
      margin-top: 0.5em;
    }
    .result-grid .table-header ion-col {
      font-weight: bold;
      border-bottom: 1px solid var(--ion-border-color);
    }
    .result-grid ion-col {
      padding: 0.4em 0;
    }

    .onboarding-footer {
      display: flex;
      justify-content: space-between;
      width: 100%;
      max-width: 400px;
      gap: 0.5em;
    }

    /* Niche selection card styles */
    .niche-card {
      text-align: center;
      padding: 0;
      margin: 4px;
      cursor: pointer;
      text-align: center;
      border-radius: var(--radius-md);
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .niche-card.selected {
      border: 2px solid var(--ion-color-primary);
    }
    .niche-card.loading {
      opacity: 0.7;
      pointer-events: none;
    }

    /* Spinner sizing inside a niche card */
    .spinner-md {
      width: 2em;
      height: 2em;
    }

    /* Niche step — make the niche list scrollable */
    .niche-step {
      align-self: stretch;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .niche-scroll-area {
      height: 203px;
      flex: 1 1 auto;
      overflow-y: auto;
      min-height: 0;
      width: 100%;
    }
  `]
})
export class OnboardingStepperComponent {
  private modalCtrl = inject(ModalController);
  readonly nicheService = inject(NicheService);
  private transloco = inject(TranslocoService);

  currentStep = signal(0);
  readonly sampleInstruction = computed(() => this.transloco.translate('onboarding.sampleInstruction'));
  sampleRunning = false;
  sampleComplete = false;
  stepIndices = [0, 1, 2, 3];

  private stepTitleKeys = ['onboarding.steps.1', 'onboarding.steps.2', 'onboarding.steps.3', 'onboarding.steps.4'];
  stepTitle = computed(() => this.stepTitleKeys[this.currentStep()]);

  // ── Resolved niche signals from the service ──
  readonly currentNiche = this.nicheService.currentNiche;
  readonly availableNiches = this.nicheService.availableNiches;

  async runSample() {
    this.sampleRunning = true;
    this.sampleComplete = false;
    await this.sleep(800);
    await this.sleep(1000);
    await this.sleep(1200);
    this.sampleRunning = false;
    this.sampleComplete = true;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  isNextDisabled(): boolean {
    if (this.currentStep() === 2 && !this.sampleComplete) return true;
    return false;
  }

  nextLabel(): string {
    return this.currentStep() === 3 ? 'onboarding.gotIt' : 'onboarding.next';
  }

  nextStep() {
    if (this.currentStep() < 3) {
      this.currentStep.update(v => v + 1);
    } else {
      this.modalCtrl.dismiss({ completed: true });
    }
  }

  skip() {
    this.modalCtrl.dismiss({ completed: true });
  }

  async selectNiche(niche: ResolvedNiche) {
    // Guard against concurrent updates
    if (this.nicheService.updatingNicheId()) return;
    await this.nicheService.updateUserNiche(niche.id);
  }
}
